import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBooking } from '../context/BookingContext';
import { useLanguage } from '../context/LanguageContext';
import TripCard from '../components/TripCard';
import StepIndicator from '../components/StepIndicator';
import SearchForm from '../components/SearchForm';
import { generateTrips } from '../data/routes';
import { formatDate, formatDateShort } from '../data/dateLocale';
import { Search, ChevronRight } from 'lucide-react';

const SearchResultsPage = () => {
  const navigate = useNavigate();
  const { searchParams, setSelectedTrip, setSelectedReturnTrip, setCurrentStep } = useBooking();
  const { t, lang } = useLanguage();
  const [sortBy, setSortBy] = useState('departure');
  const [phase, setPhase] = useState('outbound');
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  useEffect(() => { setCurrentStep(1); }, [setCurrentStep]);

  const outboundTrips = useMemo(
    () => generateTrips(searchParams.from, searchParams.to, searchParams.departureDate, lang),
    [searchParams.from, searchParams.to, searchParams.departureDate, lang]
  );
  const returnTrips = useMemo(
    () => searchParams.returnDate ? generateTrips(searchParams.to, searchParams.from, searchParams.returnDate, lang) : [],
    [searchParams.to, searchParams.from, searchParams.returnDate, lang]
  );

  const currentTrips = phase === 'outbound' ? outboundTrips : returnTrips;
  const sortedTrips = useMemo(() => {
    const sorted = [...currentTrips];
    if (sortBy === 'price') sorted.sort((a, b) => a.price - b.price);
    else if (sortBy === 'departure') sorted.sort((a, b) => a.departureTime.localeCompare(b.departureTime));
    else if (sortBy === 'arrival') sorted.sort((a, b) => a.arrivalTime.localeCompare(b.arrivalTime));
    return sorted;
  }, [currentTrips, sortBy]);

  const handleSelect = (trip, isLounge) => {
    const selected = isLounge ? { ...trip, price: trip.priceLounge, type: 'LUX Lounge' } : trip;
    if (phase === 'outbound') {
      setSelectedTrip(selected);
      if (searchParams.returnDate) { setPhase('return'); return; }
    } else {
      setSelectedReturnTrip(selected);
    }
    navigate('/seats');
  };

  useEffect(() => {
    if (!searchParams.from || !searchParams.to) navigate('/');
  }, [searchParams.from, searchParams.to, navigate]);

  if (!searchParams.from || !searchParams.to) return null;

  return (
    <div className="min-h-screen bg-[#f0f0f1]">
      <StepIndicator />

      {/* Mobile header */}
      <div className="md:hidden bg-white border-b">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex-1 text-center">
            <div className="flex items-center justify-center gap-2 text-base font-semibold">
              <span>{searchParams.from?.split(',')[0]}</span>
              <ChevronRight size={14} className="text-[#484c55]/40" />
              <span>{searchParams.to?.split(',')[0]}</span>
            </div>
            <p className="text-xs text-[#777] mt-0.5">
              {formatDateShort(searchParams.departureDate, lang)}, {Object.values(searchParams.passengers).reduce((a, b) => a + b, 0)} {t('search.passengers')}
            </p>
          </div>
          <button onClick={() => setMobileSearchOpen(!mobileSearchOpen)} className="w-9 h-9 rounded-full bg-gray-50 flex items-center justify-center flex-shrink-0">
            {mobileSearchOpen
              ? <span className="text-[#555] text-lg font-bold">×</span>
              : <Search size={18} className="text-[#555]" />
            }
          </button>
        </div>
      </div>

      {/* Mobile search overlay from top */}
      {mobileSearchOpen && (
        <div className="md:hidden fixed inset-0 z-[60] overflow-y-auto">
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black/40 z-[60]" onClick={() => setMobileSearchOpen(false)} />
          {/* Form sliding from top */}
          <div className="relative bg-white shadow-2xl rounded-b-2xl animate-slideDown z-[70] min-h-0">
            <div className="flex items-center justify-between px-4 pt-4 pb-2">
              <h3 className="text-lg font-bold text-[#484c55]">{t('results.changeSearch')}</h3>
              <button onClick={() => setMobileSearchOpen(false)} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                <span className="text-[#484c55] text-lg font-bold">×</span>
              </button>
            </div>
            <div className="px-3 pb-4">
              <SearchForm onSearchDone={() => setMobileSearchOpen(false)} />
            </div>
          </div>
        </div>
      )}

      {/* Desktop inline search bar - compact version */}
      <div className="hidden md:block bg-white border-b">
        <div className="max-w-[1280px] mx-auto">
          <SearchForm compact />
        </div>
      </div>

      <div className="max-w-[1280px] mx-auto px-3 md:px-4 py-6 md:py-8">
        <div className="flex flex-col md:flex-row gap-8">
          <div className="flex-1">
            {/* Desktop title + sort */}
            <div className="hidden md:flex items-center justify-between mb-6">
              <div>
                <h1 className="text-xl font-bold">
                  {phase === 'outbound' ? t('results.selectOutbound') : t('results.selectReturn')}
                </h1>
                <p className="text-xs text-[#777] mt-1">
                  {phase === 'outbound' ? `${searchParams.from} → ${searchParams.to}` : `${searchParams.to} → ${searchParams.from}`}
                  {' · '}
                  {formatDate(phase === 'outbound' ? searchParams.departureDate : searchParams.returnDate, lang)}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-[#777]">{t('results.sortBy')}</span>
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="text-xs font-medium border rounded px-2 py-1 outline-none cursor-pointer">
                  <option value="departure">{t('results.sortTime')}</option>
                  <option value="price">{t('results.sortPrice')}</option>
                  <option value="arrival">{t('results.sortArrival')}</option>
                </select>
              </div>
            </div>

            {/* Mobile title + sort + trip count */}
            <div className="md:hidden mb-4">
              <h1 className="text-xl font-bold mb-2">
                {phase === 'outbound' ? t('results.selectOutbound') : t('results.selectReturn')}
              </h1>
              <p className="text-[11px] text-[#484c55]/40 mb-2">{t('results.sortBy')}</p>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="w-full text-sm border rounded px-3 py-2 outline-none cursor-pointer bg-white mb-3">
                <option value="departure">{t('results.sortTime')}</option>
                <option value="price">{t('results.sortPrice')}</option>
                <option value="arrival">{t('results.sortArrival')}</option>
              </select>
              <p className="text-sm text-[#555]">
                <span className="font-semibold text-[#484c55]">{sortedTrips.length}</span> {t('trip.directTrips')}
              </p>
            </div>

            <div className="space-y-3">
              {sortedTrips.map((trip) => (
                <TripCard key={trip.id} trip={trip} onSelect={handleSelect} passengerCount={Object.values(searchParams.passengers).reduce((a, b) => a + b, 0)} />
              ))}
            </div>

            {sortedTrips.length === 0 && (
              <div className="text-center py-16">
                <p className="text-lg font-semibold text-[#484c55]/40">{t('results.noTrips')}</p>
                <p className="text-sm text-[#484c55]/30 mt-2">{t('results.tryOtherDate')}</p>
              </div>
            )}
          </div>

          <div className="w-full md:w-[300px] flex-shrink-0">
            <div className="bg-white rounded-xl overflow-hidden shadow-sm">
              <div className="relative">
                <img src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=300&h=200&fit=crop" alt="Driver" className="w-full h-[180px] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <p className="text-white font-bold text-sm uppercase tracking-wider">{t('results.driverThanks')}</p>
                </div>
              </div>
              <div className="p-4">
                <p className="text-xs text-[#555] leading-relaxed">{t('results.driverDesc')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchResultsPage;
